import { z } from 'zod';
// ─── Доска записи (Excalidraw) ──────────────────────────────────────────────
//
// У записи одна доска. Сервер хранит сцену Excalidraw как есть и не разбирает
// элементы: формат принадлежит Excalidraw и меняется вместе с его версией.
// Проверяем только общую форму и размер.
//
// `version` растёт на каждое сохранение. Клиент присылает версию, от которой
// начинал правку (`baseVersion`); если с тех пор доску сохранил кто-то ещё,
// сервер отвечает 409 и не затирает чужие изменения.
/** Сколько элементов допускаем на одной доске. */
export const WHITEBOARD_MAX_ELEMENTS = 5000;
const ORDER_KEY_DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
/**
 * Допустим ли ключ порядка слоёв (`index` элемента Excalidraw, формат
 * fractional-indexing). С битым ключом Excalidraw падает при каждой попытке
 * добавить фигуру — у всех, кто на доске, поэтому такие элементы не принимаем.
 */
export function isValidOrderKey(key) {
    if (typeof key !== 'string' || key.length === 0)
        return false;
    const head = key.charCodeAt(0);
    let intLength;
    if (head >= 97 && head <= 122)
        intLength = head - 97 + 2; // a..z
    else if (head >= 65 && head <= 90)
        intLength = 90 - head + 2; // A..Z
    else
        return false;
    if (key.length < intLength)
        return false;
    for (let i = 1; i < key.length; i++) {
        if (!ORDER_KEY_DIGITS.includes(key[i]))
            return false;
    }
    if (key === `A${'0'.repeat(26)}`)
        return false;
    return !key.slice(intLength).endsWith('0');
}
/**
 * Элемент сцены. Формат принадлежит Excalidraw, поэтому проверяем только поля,
 * на которые опираются слияние правок и порядок слоёв; остальное — как есть.
 */
export const WhiteboardElementSchema = z
    .object({
    id: z.string().min(1).max(200),
    type: z.string().min(1).max(50),
    version: z.number().int().nonnegative(),
    versionNonce: z.number().int().optional(),
    index: z
        .string()
        .refine(isValidOrderKey, 'Некорректный порядок слоёв')
        .nullable()
        .optional(),
    isDeleted: z.boolean().optional(),
})
    .passthrough();
// ─── Картинки ──────────────────────────────────────────────────────────────
//
// Картинки хранятся отдельно от сцены (таблица item_whiteboard_files): в
// элементе-картинке только fileId. Каждая загружается своим запросом, так что
// лимит запроса касается одной картинки, а не всей доски.
export const WHITEBOARD_IMAGE_MIME_TYPES = [
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'image/svg+xml',
];
/** Картинка до 7 МБ: в base64 она вырастает на треть и должна влезть в 10 МБ запроса. */
export const WHITEBOARD_FILE_MAX_BYTES = 7 * 1024 * 1024;
export const WhiteboardFileSchema = z.object({
    id: z.string().min(1).max(200),
    mimeType: z.enum(WHITEBOARD_IMAGE_MIME_TYPES),
    dataURL: z.string().startsWith('data:'),
    created: z.number(),
});
export const UploadWhiteboardFileRequestSchema = WhiteboardFileSchema.pick({
    mimeType: true,
    dataURL: true,
});
// ─── Доска ─────────────────────────────────────────────────────────────────
const UserRefSchema = z.object({ id: z.string().uuid(), displayName: z.string() });
export const WhiteboardSchema = z.object({
    itemId: z.string().uuid(),
    elements: z.array(WhiteboardElementSchema),
    /** 0 — доску ещё не сохраняли. */
    version: z.number().int().nonnegative(),
    updatedAt: z.string().nullable(),
    updatedBy: UserRefSchema.nullable(),
    /** Заблокирована: править могут только автор записи и админ пространства. */
    locked: z.boolean(),
    lockedBy: UserRefSchema.nullable(),
    /** Может ли текущий пользователь править (роль + блокировка). */
    canEdit: z.boolean(),
    /** Может ли текущий пользователь блокировать и разблокировать доску. */
    canLock: z.boolean(),
});
export const SaveWhiteboardRequestSchema = z.object({
    elements: z.array(WhiteboardElementSchema).max(WHITEBOARD_MAX_ELEMENTS),
    baseVersion: z.number().int().nonnegative(),
});
export const SetWhiteboardLockRequestSchema = z.object({ locked: z.boolean() });
/** Какие записи пространства с непустой доской — для отметки в таблице и на канбане. */
export const WhiteboardSummarySchema = z.object({
    boards: z.record(z.object({
        elementCount: z.number().int().nonnegative(),
        updatedAt: z.string(),
    })),
});
// ─── История версий ────────────────────────────────────────────────────────
//
// Снимок доски: раз в 10 минут работы и прежнее состояние перед крупным
// удалением или восстановлением. Хранятся последние 100 снимков доски.
export const WhiteboardVersionReasonSchema = z.enum([
    'auto',
    'before_delete',
    'before_restore',
    'before_import',
]);
export const WhiteboardVersionSummarySchema = z.object({
    id: z.string().uuid(),
    createdAt: z.string(),
    createdBy: UserRefSchema.nullable(),
    reason: WhiteboardVersionReasonSchema,
    elementCount: z.number().int().nonnegative(),
});
export const WhiteboardVersionSchema = WhiteboardVersionSummarySchema.extend({
    elements: z.array(WhiteboardElementSchema),
});
/** Отметить снимок «перед импортом» — клиент вызывает перед заменой содержимого. */
export const CreateWhiteboardVersionRequestSchema = z.object({
    reason: z.literal('before_import'),
});
// ─── Совместная работа: сообщения по WebSocket ─────────────────────────────
//
// Подключение: GET /api/spaces/:id/items/:itemId/whiteboard/live (Upgrade).
// Все, кто открыл доску, попадают в одну комнату. Клиент шлёт только
// изменившиеся элементы; сервер сливает их по версии элемента (выше версия —
// новее; при равной побеждает меньший versionNonce — как в самом Excalidraw),
// раздаёт остальным и сам сохраняет доску в базу.
export const WhiteboardPeerSchema = z.object({
    socketId: z.string(),
    userId: z.string().uuid(),
    displayName: z.string(),
    canEdit: z.boolean(),
});
export const WhiteboardPointerSchema = z.object({
    x: z.number(),
    y: z.number(),
    tool: z.enum(['pointer', 'laser']),
    button: z.enum(['up', 'down']),
});
/** Клиент → сервер. */
export const WhiteboardClientMessageSchema = z.discriminatedUnion('t', [
    z.object({
        t: z.literal('update'),
        elements: z.array(WhiteboardElementSchema).max(WHITEBOARD_MAX_ELEMENTS),
    }),
    z.object({
        t: z.literal('pointer'),
        pointer: WhiteboardPointerSchema,
        selected: z.record(z.literal(true)).optional(),
    }),
    z.object({ t: z.literal('ping') }),
    /** Следовать за человеком (null — перестать). */
    z.object({ t: z.literal('follow'), target: z.string().max(100).nullable() }),
    /** Видимая область того, за кем следят: [minX, minY, maxX, maxY] сцены. */
    z.object({
        t: z.literal('viewport'),
        bounds: z.tuple([z.number(), z.number(), z.number(), z.number()]),
    }),
    /** «Вести за собой»: все на доске следуют за этим человеком. */
    z.object({ t: z.literal('lead'), on: z.boolean() }),
]);
// ─── Шаблоны досок ─────────────────────────────────────────────────────────
//
// Встроенные шаблоны живут в коде фронта; здесь — сохранённые командой
// шаблоны пространства (из всей доски или выделенного). Картинки шаблона
// копируются вместе с ним.
export const WhiteboardTemplateSummarySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    elementCount: z.number().int().nonnegative(),
    createdBy: UserRefSchema.nullable(),
    updatedAt: z.string(),
    /** Может ли текущий пользователь удалить шаблон (автор или админ). */
    canDelete: z.boolean(),
});
export const WhiteboardTemplateSchema = WhiteboardTemplateSummarySchema.extend({
    elements: z.array(WhiteboardElementSchema),
});
export const CreateWhiteboardTemplateRequestSchema = z.object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).optional(),
    /** С доски какой записи брать элементы. */
    fromItemId: z.string().uuid(),
    /** Только эти элементы (и их подписи); без поля — вся доска. */
    elementIds: z.array(z.string().min(1).max(200)).max(WHITEBOARD_MAX_ELEMENTS).optional(),
});
// ─── Доска по описанию (MCP: перенос картинки на доску) ────────────────────
//
// Простое описание вместо формата Excalidraw: блоки с координатами и текстом
// и стрелки между ними. Сервер сам собирает элементы — подписи внутри фигур,
// привязанные стрелки, порядок слоёв. Координаты — в пикселях (например,
// исходной картинки): x вправо, y вниз, (x, y) — левый верхний угол блока.
export const WHITEBOARD_SPEC_COLORS = [
    'yellow',
    'green',
    'red',
    'blue',
    'violet',
    'orange',
    'gray',
    'white',
    'dark',
    'none',
];
const SpecColorSchema = z.union([
    z.enum(WHITEBOARD_SPEC_COLORS),
    z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Цвет — имя из списка или #RRGGBB'),
]);
export const WhiteboardSpecNodeSchema = z.object({
    /** Свой id блока — на него ссылаются стрелки. */
    id: z.string().min(1).max(64),
    /**
     * sticky — стикер; box — прямоугольник с текстом; ellipse, diamond — фигуры;
     * text — просто текст; frame — рамка-группа с подписью сверху (title);
     * code — тёмный блок моноширинного текста с заголовком (title).
     */
    kind: z.enum(['sticky', 'box', 'ellipse', 'diamond', 'text', 'frame', 'code']),
    x: z.number().min(-100_000).max(100_000),
    y: z.number().min(-100_000).max(100_000),
    /** Без размеров — подберутся по тексту. */
    width: z.number().positive().max(10_000).optional(),
    height: z.number().positive().max(10_000).optional(),
    text: z.string().max(4000).optional(),
    /** Подпись рамки / заголовок блока кода. */
    title: z.string().max(200).optional(),
    /** Заливка: имя или #RRGGBB. У текста — цвет букв. */
    color: SpecColorSchema.optional(),
    fontSize: z.number().min(6).max(96).optional(),
    align: z.enum(['left', 'center']).optional(),
});
export const WhiteboardSpecEdgeSchema = z
    .object({
    /** id блоков; стрелка привяжется к ним и будет двигаться вместе с ними. */
    from: z.string().max(64).optional(),
    to: z.string().max(64).optional(),
    /** Свой маршрут (абсолютные точки), например петля назад. */
    points: z.array(z.tuple([z.number(), z.number()])).min(2).max(50).optional(),
    label: z.string().max(200).optional(),
    dashed: z.boolean().optional(),
    /** false — линия без наконечника. */
    arrow: z.boolean().optional(),
})
    .refine((e) => (e.from && e.to) || e.points, 'Нужны from и to или points');
export const WhiteboardSpecSchema = z.object({
    /** Крупный заголовок над доской (необязательно). */
    title: z.string().max(200).optional(),
    nodes: z.array(WhiteboardSpecNodeSchema).max(1000).default([]),
    edges: z.array(WhiteboardSpecEdgeSchema).max(1000).default([]),
});
export const DrawWhiteboardRequestSchema = z.object({
    spec: WhiteboardSpecSchema,
    /** replace — заменить содержимое (прежнее — в историю); append — дописать справа. */
    mode: z.enum(['replace', 'append']).default('append'),
});
